import React from 'react';
import { Box, Typography, Chip, Tooltip, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import AnimatedNumber from '../shared/AnimatedNumber';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import { RADIUS } from '../../theme/tokens';

// Coin SVG — gradient stops sourced from theme accent at call site
const CoinIcon = ({ size = 20, colorStart, colorMid, colorEnd }) => {
  const theme = useTheme();
  const accent = theme.palette.accent?.main ?? theme.palette.warning.main;
  // Coin gold palette derived from theme accent; lighter/darker tints produced inline
  const c1 = colorStart ?? accent;
  const c2 = colorMid ?? theme.palette.warning.main;
  const c3 = colorEnd ?? accent;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="url(#bmlCoinGrad)" />
      <text x="12" y="16" textAnchor="middle" fill="rgba(0,0,0,0.55)" fontSize="10" fontWeight="bold">B</text>
      <defs>
        <linearGradient id="bmlCoinGrad" x1="2" y1="2" x2="22" y2="22" gradientUnits="userSpaceOnUse">
          <stop stopColor={c1} />
          <stop offset="0.5" stopColor={c2} />
          <stop offset="1" stopColor={c3} />
        </linearGradient>
      </defs>
    </svg>
  );
};

/**
 * BMLCoinBalance
 * Displays the user's coin balance in multiple visual variants.
 * Consumes theme tokens only — no hardcoded hex colors.
 *
 * @param {number} balance
 * @param {boolean} [loading]
 * @param {'default'|'chip'|'large'} [variant]
 * @param {() => void} [onClick]
 * @param {boolean} [showTooltip]
 */
const BMLCoinBalance = ({ balance, loading = false, variant = 'default', onClick, showTooltip = true }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const reducedMotion = useReducedMotionPreferred();

  const accentColor = theme.palette.accent?.main ?? theme.palette.warning.main;
  // Adaptive tint works in both light/dark by referencing theme background
  const tintBg = theme.palette.mode === 'light'
    ? 'rgba(247,179,43,0.12)'
    : 'rgba(255,202,92,0.14)';
  const tintBorder = theme.palette.mode === 'light'
    ? 'rgba(247,179,43,0.4)'
    : 'rgba(255,202,92,0.3)';
  const coinTextColor = theme.palette.mode === 'light'
    ? 'rgba(120,80,0,0.9)'
    : accentColor;

  if (loading) {
    return (
      <Box
        sx={{
          width: 80,
          height: 32,
          borderRadius: RADIUS.sm,
          bgcolor: tintBg,
          animation: 'none',
        }}
      />
    );
  }

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate('/student/coins');
    }
  };

  const hoverAnim = reducedMotion ? {} : { scale: 1.04 };
  const tapAnim = reducedMotion ? {} : { scale: 0.96 };

  const content = (
    <motion.div
      whileHover={hoverAnim}
      whileTap={tapAnim}
      style={{ cursor: 'pointer', display: 'inline-block' }}
      onClick={handleClick}
    >
      {variant === 'chip' ? (
        <Chip
          icon={<CoinIcon size={18} />}
          label={<AnimatedNumber value={balance ?? 0} format={(n) => Math.round(n).toLocaleString('en-IN')} />}
          sx={{
            bgcolor: tintBg,
            border: `1px solid ${tintBorder}`,
            fontWeight: 700,
            fontSize: '0.875rem',
            color: coinTextColor,
            '& .MuiChip-icon': { marginLeft: '4px' },
          }}
        />
      ) : variant === 'large' ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: tintBg,
            border: `2px solid ${tintBorder}`,
            borderRadius: RADIUS.lg,
            px: 2,
            py: 1,
          }}
        >
          <motion.div
            animate={reducedMotion ? {} : { rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <CoinIcon size={28} />
          </motion.div>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
              BML Coins
            </Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color: coinTextColor, lineHeight: 1 }}>
              <AnimatedNumber value={balance ?? 0} format={(n) => Math.round(n).toLocaleString('en-IN')} />
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: tintBg,
            border: `1px solid ${tintBorder}`,
            borderRadius: RADIUS.sm,
            px: 1.5,
            py: 0.5,
          }}
        >
          <CoinIcon size={18} />
          <Typography fontWeight={700} sx={{ color: coinTextColor }}>
            <AnimatedNumber value={balance ?? 0} format={(n) => Math.round(n).toLocaleString('en-IN')} />
          </Typography>
        </Box>
      )}
    </motion.div>
  );

  if (showTooltip) {
    return (
      <Tooltip title="View your BML Coins" arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

export { CoinIcon };
export default BMLCoinBalance;
